import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Option } from '@fluentui/react-components';
import { ClipboardTaskListLtr20Regular } from '@fluentui/react-icons';
import { ControlledField } from '@shared/components/ControlledField';
import { FormDialog } from '@shared/components/dialogs/FormDialog';
import { StatusLabel } from '@shared/components/StatusLabel';
import { useUsers } from '@modules/users/api/users.api';
import { dateToYMD } from '@shared/utils/date';
import { useInvalidDateInputs } from '@shared/hooks/useInvalidDateInputs.hook';
import { INVALID_DATE_MESSAGE } from '@shared/schemas/calendarDate.schema';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import type { TProject } from '../../types';
import { TASK_LINKED_ITEMS_MAX_COUNT, TASK_PRIORITY_LABELS } from '../constants';
import { useExcludedAssigneeIds } from '../useExcludedAssigneeIds.hook';
import { getLinkedItemChanges, resolveLinkedItems, toTaskLinkedItem } from '../linkedItems.helper';
import {
  ETaskPriority,
  type TTaskDetails,
  type TTaskFormValues,
  type TTaskValues,
  type TUpdateTaskDetails,
} from '../types';
import { DEFAULT_TASK_FORM, taskFormSchema } from './taskForm';
import TaskLinkedItemsField from './TaskLinkedItemsField';

type TProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  project: TProject;
  /** The task to edit, with the items its links resolve to. */
  details?: TTaskDetails;
  rootItemId: string;
  onSave: (values: TTaskValues, changes: TUpdateTaskDetails) => Promise<unknown>;
};

const TaskFormDialog = ({ isOpen, setIsOpen, project, details, rootItemId, onSave }: TProps) => {
  const task = details?.task;
  const savedLinkedItems = useMemo(
    () => (details ? resolveLinkedItems(details.task.linkedItems, details.resolvedItems) : []),
    [details]
  );
  const { userMap, error: usersError } = useUsers();
  const { hasInvalidDateInput, onInvalidDateInput, resetInvalidDateInputs } = useInvalidDateInputs();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields, isSubmitting },
  } = useForm<TTaskFormValues>({
    resolver: yupResolver(taskFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_TASK_FORM,
  });
  const excludedUserIds = useExcludedAssigneeIds(project, userMap);
  const linkedItems = useWatch({ control, name: 'linkedItems' });
  const { addedLinkedItems = [], removedLinkedItems = [] } = getLinkedItemChanges(savedLinkedItems, linkedItems);
  const hasTooManyLinkChanges =
    Math.max(addedLinkedItems.length, removedLinkedItems.length) > TASK_LINKED_ITEMS_MAX_COUNT;
  useEffect(() => {
    if (!isOpen) return;
    resetInvalidDateInputs();
    reset(
      task
        ? {
            title: task.title,
            description: task.description ?? '',
            assignee: task.assignee ?? null,
            dueDate: dateToYMD(task.dueDate) || null,
            priority: [task.priority],
            linkedItems: savedLinkedItems,
          }
        : DEFAULT_TASK_FORM
    );
  }, [isOpen, task, savedLinkedItems, reset, resetInvalidDateInputs]);
  const onSubmit = async (form: TTaskFormValues) => {
    const values: TTaskValues = {
      ...form,
      description: form.description || null,
      assignee: form.assignee || null,
      priority: form.priority[0],
      linkedItems: form.linkedItems.map(toTaskLinkedItem),
    };
    const changes: TUpdateTaskDetails = {
      ...Object.fromEntries(
        Object.keys(dirtyFields)
          .filter((name) => name !== 'linkedItems')
          .map((name) => [name, values[name as keyof TTaskValues]])
      ),
      ...getLinkedItemChanges(savedLinkedItems, form.linkedItems),
    };
    try {
      await onSave(values, changes);
      setIsOpen(false);
    } catch {
      /* The API hook reports the failure. Preserve the draft for retry. */
    }
  };
  return (
    <FormDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={
        <div className="flex items-center space-x-2">
          <ClipboardTaskListLtr20Regular className="shrink-0" />
          <span className="min-w-0 break-words">
            {task ? 'Edit' : 'Create'} {TERMINOLOGY.task.lower}
          </span>
        </div>
      }
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={task ? 'Save' : 'Create'}
      submitIcon={null}
      isSubmitting={isSubmitting}
      submitDisabled={!userMap || !!usersError || hasInvalidDateInput || hasTooManyLinkChanges}
      surfaceClassName="!max-w-[960px]"
      contentClassName="max-h-[70vh] overflow-y-auto"
    >
      <StatusLabel
        data={userMap}
        error={usersError}
        errorMessage="Loading users failed!"
        render={() => (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <ControlledField
                inputType="input"
                name="title"
                label="Title"
                placeholder={`Enter ${TERMINOLOGY.task.lower} title`}
                fieldClassName="lg:col-span-3 min-w-0"
                required
                control={control}
                errors={errors}
              />
              <Controller
                name="linkedItems"
                control={control}
                render={({ field }) => (
                  <div className="min-w-0 lg:col-span-3">
                    <TaskLinkedItemsField value={field.value} onChange={field.onChange} rootItemId={rootItemId} />
                  </div>
                )}
              />
              <ControlledField
                inputType="textarea"
                name="description"
                label="Description"
                rows={4}
                placeholder="Add a description"
                fieldClassName="lg:col-span-3 min-w-0"
                resize="vertical"
                control={control}
                errors={errors}
              />
              <ControlledField
                inputType="multiuserselect"
                fieldClassName="min-w-0 grid-rows-[auto_1fr_auto]"
                size="medium"
                isSingle
                name="assignee"
                label="Assignee"
                excludedUserIds={excludedUserIds}
                control={control}
                errors={errors}
              />
              <ControlledField
                inputType="date"
                valueFormat="day"
                fieldClassName="min-w-0 grid-rows-[auto_1fr_auto]"
                clearable
                name="dueDate"
                label="Due date"
                control={control}
                errors={errors}
                onInvalidInputChange={onInvalidDateInput('dueDate')}
              />
              <ControlledField
                inputType="dropdown"
                name="priority"
                fieldClassName="min-w-0 grid-rows-[auto_1fr_auto]"
                className="!min-w-0 w-full"
                label="Priority"
                control={control}
                errors={errors}
                getLabel={(value) => TASK_PRIORITY_LABELS[value as ETaskPriority]}
              >
                {Object.values(ETaskPriority).map((value) => (
                  <Option key={value} value={value} text={TASK_PRIORITY_LABELS[value]}>
                    {TASK_PRIORITY_LABELS[value]}
                  </Option>
                ))}
              </ControlledField>
            </div>
            {hasInvalidDateInput && <p className="text-red-500">{INVALID_DATE_MESSAGE}</p>}
            {hasTooManyLinkChanges && (
              <p className="text-red-500">Link or remove at most {TASK_LINKED_ITEMS_MAX_COUNT} items in one save.</p>
            )}
          </div>
        )}
      />
    </FormDialog>
  );
};
export default TaskFormDialog;
